import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  downloadAccountDocument,
  type BrokerageBond,
} from "@/lib/brokerAccounts";
import {
  bondFlowOf,
  type BondListDecision,
  type BondSubmissionListItem,
} from "@/lib/brokerBonds";
import { DECISION_LABEL } from "@/lib/bondListLabels";
import { messageFor } from "@/lib/errors";
import {
  bondStatusBadgeClass,
  bondStatusLabel,
  statusBadgeClass,
} from "@/lib/listFormat";
import { saveFile } from "@/lib/downloadFile";

// The Surety lists' cells (BRK-S253), shared by the bond submissions and the
// issued bonds list definitions, so a status, a decision or a download can't
// read differently between them.

const CHIP =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";

/** NULL IS ITS OWN STATE, and not `pending`: a draft has had no decision,
 *  where `pending` means one has not landed yet (BRK-S174). */
export function BondDecisionChip({
  decision,
}: {
  decision: BondListDecision | null | undefined;
}) {
  return decision ? (
    <span className={`${CHIP} ${statusBadgeClass(decision)}`}>
      {DECISION_LABEL[decision]}
    </span>
  ) : (
    <span className="text-brk-dim">—</span>
  );
}

/** The order's status, with referral as its own chip: it is orthogonal to
 *  status (a draft can be referred and a filed order can not be). A submitted
 *  STANDALONE pre-approval reads "Complete", never "Submitted" (BRK-S160).
 *  The v2 `status` says where the order stands; the `workflowStatus` chip
 *  that rode beside it left with the field (PMS-S837, BRK-S511). */
export function BondStatusChips({
  item,
}: {
  item: Pick<BondSubmissionListItem, "status" | "referred" | "flow">;
}) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span
        className={`${CHIP} ${bondStatusBadgeClass(item.status, bondFlowOf(item))}`}
      >
        {bondStatusLabel(item.status, bondFlowOf(item))}
      </span>
      {item.referred && (
        <span className={`${CHIP} ${statusBadgeClass("referred")}`}>
          Referred
        </span>
      )}
    </span>
  );
}

/** The executed bond's download, through its ACCOUNT's document route. A bond
 *  whose filing hasn't landed has no document yet, so there is nothing to
 *  press, and the row says why (BRK-S181). */
export function IssuedBondDownload({ bond }: { bond: BrokerageBond }) {
  const { authedBlob } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!bond.documentId) {
    return <span className="text-xs text-brk-dim">Filing not in yet</span>;
  }
  const documentId = bond.documentId;

  async function download() {
    setBusy(true);
    setError(null);
    try {
      saveFile(
        await downloadAccountDocument(authedBlob, bond.accountId, documentId),
        `${bond.bondNumber ?? bond.bondId}.pdf`,
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-2">
      <button
        type="button"
        onClick={download}
        disabled={busy}
        aria-label={`Download bond ${bond.bondNumber ?? bond.bondId}`}
        className="shrink-0 rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? "Preparing…" : "Download"}
      </button>
      {error && (
        <span role="alert" className="text-xs text-brk-danger">
          {error}
        </span>
      )}
    </span>
  );
}
